import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { ThanksPrintTemplate } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  formatDeliveryDate,
  formatDeliveryDateShort,
  toDeliveryDateKey,
} from '@service/giftStore/utils/giftDeliveryDate.util';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { statusLabel, typeLabel } from '../../giftSeller/utils/delivery.constants';
import {
  formatPhone,
  getBrandItemName,
  getOrdererInfo,
} from '../../giftSeller/utils/delivery.utils';

// ─────────────────────────────────────────────
// 주문 배송관리 발주서 엑셀 다운로드
// 선택한 배송 건 1건당 1행. 배송지·상품옵션·감사카드 문구까지 포함해
// 출고 담당자가 이 파일 하나로 포장/인쇄/송장 작업을 할 수 있게 한다.
// ─────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

const fmtDT = (d?: Date | string): string => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/** 파일명에 못 쓰는 문자(\ / : * ? " < > |)를 _로 바꾸고 앞뒤 공백을 정리한다. */
const safeFileNamePart = (s: string): string =>
  s
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();

/** 한글 2.2칸/영문 1칸 기준으로 컬럼 폭 자동 조정 (줄바꿈 문구는 가장 긴 줄 기준) */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 6;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v != null) {
        for (const line of String(cell.v).split('\n')) {
          let w = 0;
          for (const ch of line) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
          if (w > max) max = w;
        }
      }
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 3, 50) });
  }
  ws['!cols'] = cols;
}

/** 빈 번호는 '-' 대신 빈칸 */
const phoneCell = (phone?: string): string => {
  if (!phone || phone === '-') return '';
  return formatPhone(phone);
};

const brandName = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftItemBrand === 'object' && d.giftItemBrand ? d.giftItemBrand.name : '';

/** "옵션명 1: 선택지 / 옵션명 2: 선택지" 형태로 합친다 */
const optionText = (d: GiftDeliveryForAdmin): string =>
  (d.selectedOptions ?? [])
    .map(
      (opt) =>
        `${opt.optionName}${
          opt.slotIndex !== undefined ? ` ${opt.slotIndex + 1}` : ''
        }: ${opt.choiceName}`,
    )
    .join(' / ');

/**
 * 선택한 배송 건을 발주서 엑셀로 내려받는다.
 * 행 순서는 배송요청(주소 입력) 오래된 순 — 화면 순번과 같은 기준.
 * @param deliveries 대상 배송 건
 * @param thanksTemplates 감사카드 템플릿 목록 (templateId → 템플릿명 표기용)
 * @param options.includeDesiredDate 배송희망일 열 포함 여부 — 날짜 지정을 안 쓰는 브랜드 발주서는
 *   열을 빼서 기존 열 위치를 그대로 둔다(열 위치로 읽는 업체 보호). 생략하면 날짜가 있는 건이 있을 때만.
 */
export function exportDeliveryOrderSheetExcel(
  deliveries: GiftDeliveryForAdmin[],
  thanksTemplates: ThanksPrintTemplate[] = [],
  options: { includeDesiredDate?: boolean } = {},
) {
  if (!deliveries.length) return;
  const includeDesiredDate =
    options.includeDesiredDate ??
    deliveries.some((d) => !!toDeliveryDateKey(d.desiredDeliveryDate));

  const templateName = new Map<string, string>();
  for (const t of thanksTemplates) {
    if (t._id && t.name) templateName.set(t._id, t.name);
  }

  const submittedMs = (d: GiftDeliveryForAdmin) => {
    const t = d.recipient?.submittedAt
      ? new Date(d.recipient.submittedAt).getTime()
      : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const sorted = [...deliveries].sort((a, b) => submittedMs(a) - submittedMs(b));

  const rows: Record<string, unknown>[] = sorted.map((d, idx) => {
    const orderer = getOrdererInfo(d);
    const recipient = d.recipient;
    const thanks = d.thanksPrint;
    return {
      순번: idx + 1,
      배송요청일시: fmtDT(recipient?.submittedAt),
      // 받는 날 (날짜 지정 브랜드 주문만 값이 있다). 입금 지연으로 자동 변경된 건은 바뀐 날짜 + 어디서 바뀌었는지
      ...(includeDesiredDate && {
        배송희망일:
          formatDeliveryDate(d.desiredDeliveryDate) +
          (d.desiredDeliveryDate &&
          d.desiredDeliveryDateLate &&
          d.desiredDeliveryDateRequested !== d.desiredDeliveryDate
            ? ` (입금 지연·${
                formatDeliveryDateShort(d.desiredDeliveryDateRequested) || '날짜'
              }에서 변경)`
            : ''),
      }),
      브랜드: brandName(d),
      상품: getBrandItemName(d),
      상품옵션: optionText(d),
      수량: d.quantity,
      배송형태: typeLabel[d.type] || d.type,
      수령인: recipient?.name ?? '',
      수령인연락처: phoneCell(recipient?.phoneNumber),
      우편번호: recipient?.postalCode ?? '',
      주소: recipient?.address ?? '',
      상세주소: recipient?.addressDetail ?? '',
      주문자: orderer.name,
      주문자연락처: phoneCell(orderer.phone),
      // 감사카드 열 이름은 인쇄 모달(GiftBrandThanksCardModal)의 라벨과 맞춘다.
      // 담당자가 모달에서 복사하던 값과 엑셀 열이 같은 이름이어야 헷갈리지 않는다.
      감사카드: thanks ? templateName.get(thanks.templateId) || '있음' : '',
      감사글: thanks?.message ?? '',
      '보내는 분(배상)': thanks?.senderName ?? '',
      상태: statusLabel[d.deliveryStatus]?.text ?? d.deliveryStatus,
      코드: d.fullCode || d.code || '',
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws);
  // 첫 행(헤더) 고정 + 자동 필터
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (ws['!ref']) ws['!autofilter'] = { ref: ws['!ref'] };
  XLSX.utils.book_append_sheet(wb, ws, '발주서');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

  // 파일명: {브랜드명_}답례품_발주서_YYYY-MM-DD_HHmm.xlsx (브랜드가 하나로 통일될 때만 브랜드명 포함)
  const brands = new Set(sorted.map(brandName).filter(Boolean));
  const single = brands.size === 1 ? safeFileNamePart(Array.from(brands)[0] ?? '') : '';
  const now = new Date();
  const stamp = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(
    now.getDate(),
  )}_${pad2(now.getHours())}${pad2(now.getMinutes())}`;
  const prefix = single ? `${single}_답례품_발주서` : '답례품_발주서';
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `${prefix}_${stamp}.xlsx`,
  );
}
